import { ErrorOutline, InfoOutlined } from '@mui/icons-material';
import {
  InputAdornment,
  FormControl,
  FormHelperText,
  Input,
  InputProps,
  Box,
} from '@mui/material';

import { Label, Tooltip, HelperText } from '@components';

type MobileEnterKeyHint = 'next' | 'go';

export interface OuterLabelTextFieldProps extends InputProps {
  /** The input label. */
  label: string;
  /** The id of the field. Usually in the following format of "moduleName.fieldName". */
  fieldName: string;
  /** Indicates whether to prefix the input with a dollar sign icon. */
  currencyIcon?: boolean;
  /** Defines the enter label for virtual keyboards. */
  enterKeyHint?: MobileEnterKeyHint;
  /** Callback that is called on the key down event. */
  onKeyDown?: (event: unknown) => void;
  /**
   * The error message to be rendered beneath the input. If present, the UI state of the input will reflect the error state (red border, error icon, etc.).
   */
  errorMessage?: string;
  /**
   * Informative text to be rendered inside a tooltip. If present, the Tooltip component will render beside the label.
   */
  infoTooltipText?: string;
  /**
   * Supplemental text that is rendered underneath the label.
   */
  helperText?: string;
  /** True if the user has focus'd and then blur'd the input */
  isBlurred?: boolean;
  /** Renders a red required asterisk beside the label. */
  required?: boolean;
}

/**
 * An MUI OuterLabelTextField component that expects to be hooked up to React Hook Form. The props extend from MUI's FilledTextFieldProps.
 */

export const OuterLabelTextField = ({
  inputMode,
  enterKeyHint,
  errorMessage,
  currencyIcon,
  fieldName,
  label,
  infoTooltipText,
  helperText,
  isBlurred,
  required,
  ...props
}: OuterLabelTextFieldProps) => {
  const hasError = !!errorMessage && isBlurred;

  return (
    <>
      <FormControl sx={{ width: 1 }}>
        {label && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <div>
              <Label htmlFor={fieldName} required={required}>
                {label}
              </Label>
              {helperText && <HelperText>{helperText}</HelperText>}
            </div>

            {!!infoTooltipText && (
              <Tooltip title={infoTooltipText} placement="top-end">
                <InfoOutlined
                  sx={{
                    color: 'brandBlue.main',
                    cursor: 'pointer',
                    marginLeft: 1,
                    '@media (max-width: 960px) and (min-width: 768px)': {
                      position: 'absolute',
                      right: 0,
                      marginRight: '1vw',
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
        )}

        <Input
          {...props}
          fullWidth
          error={hasError}
          key={fieldName}
          name={fieldName}
          id={fieldName}
          inputMode={inputMode}
          inputProps={{
            enterKeyHint,
            inputMode,
            'data-cy': fieldName,
          }}
          disableUnderline
          sx={(theme) => ({
            my: 0.5,
            border: `1px solid ${theme.palette.custom.inputBorder}`,
            borderRadius: 1,
            backgroundColor: 'white',
            fontSize: '18px',
            fontWeight: 500,

            '&.MuiFilledInput-adornedStart': {
              padding: 0,
            },

            '& .MuiFilledInput-inputAdornedStart': {
              paddingLeft: 1.5,
            },

            '&:hover': {
              backgroundColor: 'white',
            },

            '&.Mui-focused': {
              backgroundColor: 'white',
              border: `2px solid ${theme.palette.brandBlue.dark}`,
              boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.25)',
            },

            '&.Mui-error': {
              borderColor: theme.palette.brandRed.dark,
            },

            '& input': {
              height: '56px',
            },

            '& input:not(.MuiInputBase-inputAdornedStart)': {
              padding: '0 12px',
            },

            '& .MuiInputAdornment-positionEnd': {
              paddingRight: '12px',
            },

            /* Extend autofill background to cover full input including dollar sign adornment */
            '&:has(input:-webkit-autofill)': {
              backgroundColor: 'rgb(232, 240, 254)',
            },
            /* Keep full blue background when autofilled field is hovered */
            '&:has(input:-webkit-autofill):hover': {
              backgroundColor: 'rgb(232, 240, 254)',
            },
            /* Keep full blue background when autofilled field is focused */
            '&:has(input:-webkit-autofill).Mui-focused': {
              backgroundColor: 'rgb(232, 240, 254)',
              border: `2px solid ${theme.palette.brandBlue.dark}`,
              boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.25)',
            },
            /* Match inner input to parent so autofill highlight is seamless across $ and input */
            '&:has(input:-webkit-autofill) input': {
              WebkitBoxShadow: '0 0 0 100px rgb(232, 240, 254) inset',
              boxShadow: '0 0 0 100px rgb(232, 240, 254) inset',
            },
          })}
          endAdornment={
            hasError && (
              <InputAdornment position="end">
                <ErrorOutline
                  sx={(theme) => ({
                    fill: theme.palette.brandRed.dark,
                  })}
                />
              </InputAdornment>
            )
          }
          startAdornment={
            currencyIcon && (
              <InputAdornment
                position="start"
                sx={{
                  marginLeft: 1.5,

                  '& .MuiTypography-root': {
                    fontWeight: 500,
                    color: 'custom.inputLabel',
                  },
                }}
              >
                $
              </InputAdornment>
            )
          }
        />
        {hasError && (
          <FormHelperText error variant="filled">
            {errorMessage}
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
};
